/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Injectable } from '@angular/core';
import { FabUser } from '@fabric/common/types';
import {DELETE_AUTHORITY, MODIFY_AUTHORITY, VIEW_AUTHORITY} from '../../common/constants';
import {AuthenticationService} from '../../core/auth/authentication.service';

/**
 * Created by michael faith on 3/12/2019.
 */

@Injectable({
  providedIn: 'root'
})
export class UserService {
  // @ts-ignore
  public user: FabUser = null;

  constructor(private readonly authenticationService: AuthenticationService) {
    // Construct the UserProfile object
    this.createUser();
  }

  /**
   * sets the user Authorities based on the given roles
   *
   * Every authenticated user gets `user-view` so non-admin users can access
   * baseline features like the home page driver search. Users with the
   * `PGA_Admin` or `PGA_SiteAdmin` AD role additionally receive the admin /
   * modify / delete authorities.
   *
   * @param roles - the users AD roles (kept for API compatibility; the role
   *                check below uses `this.user.roles` directly via hasRole())
   */
  public setUserAuthorities(roles: string[]): void {
    if (this.user) {
      // Baseline authority granted to every authenticated user.
      this.user.authorities = [VIEW_AUTHORITY.userAuthority];

      // Elevate admins (global super-admin or global site-admin) with the
      // full admin / modify / delete authority set.
      if (this.hasRole('PGA_Admin') || this.hasRole('PGA_SiteAdmin')) {
        this.user.authorities = [
          VIEW_AUTHORITY.adminAuthority, VIEW_AUTHORITY.userAuthority,
          MODIFY_AUTHORITY.adminAuthority, MODIFY_AUTHORITY.userAuthority,
          DELETE_AUTHORITY.adminAuthority, DELETE_AUTHORITY.userAuthority
        ];
      }
    }
  }

  /**
   * Test to see if the current user has any of the provided authorities
   *
   * @param requiredAuthorities Array of authority names
   */
  public hasAnyAuthorities(requiredAuthorities: string[]): boolean {
    for (const authority of requiredAuthorities) {
      if (authority !== null && this.user?.authorities && this.user.authorities.indexOf(authority) > -1) {
        return true;
      }
    }
    return false;
  }

  /**
   * Test if the current user has the provided authority.
   *
   * @param requiredAuthority Authority name
   */
  public hasAuthority(requiredAuthority: string): boolean {
    return this.hasAnyAuthorities([requiredAuthority]);
  }

  /**
   * Check to see if the current user has the provided role.
   *
   * @param requiredRole Role name
   */
  public hasRole(requiredRole: string): boolean {
    return this.user?.roles ? this.user.roles.indexOf(requiredRole) > -1 : false;
  }

  /**
   * Create an instance of FabUser using the data provided.
   */
  private createUser(): void {
    const user = this.authenticationService.userInfo;
    if (user) {
      this.user = user;
      this.user.roles ??= [];
      this.user.authorities ??= [];
      if (this.user.gmin) {
        this.user.profileImageUrl = this.getGmPhoto(this.user.gmin);
      }
    }
  }

  /**
   * Constructs the URL string for the GM photo of the user.
   * @return string containing the people finder url to the photo
   */
  private getGmPhoto(gmin: string): string {
    gmin = gmin.toString();
    const firstThree = gmin.toString().substring(0, 3);
    const secondThree = gmin.toString().substring(3, 6);
    const path = `${firstThree}/${secondThree}/${gmin}_lthumb.jpg`;
    return `https://peoplefinder.gm.com/Photos/${path}`;
  }
}
